import React, { useState, useEffect } from 'react';
import { getToken } from '../utils/auth';

const GiftTracker = () => {
  const [gifts, setGifts] = useState([]);
  const [allGuests, setAllGuests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [editingGift, setEditingGift] = useState(null);
  const [giftFormData, setGiftFormData] = useState({
    rsvp_id: '',
    amount: '',
    currency: 'CNY',
    payment_method: 'Cash',
    date_received: new Date().toISOString().split('T')[0],
    admin_notes: ''
  });

  useEffect(() => {
    fetchGifts();
    fetchStats();
    fetchAllGuests();
  }, []);

  const fetchGifts = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const response = await fetch('/api/gifts', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setGifts(data.data);
      } else {
        alert(data.error || '获取礼金记录失败');
      }
    } catch (err) {
      alert('获取礼金记录失败，请刷新页面重试');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = getToken();
      const response = await fetch('/api/gifts/stats', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setStats(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const fetchAllGuests = async () => {
    try {
      const token = getToken();
      const response = await fetch('/api/gifts/attending-guests', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setAllGuests(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch attending guests:', err);
    }
  };

  const handleAddGift = () => {
    setEditingGift(null);
    setGiftFormData({
      rsvp_id: '',
      amount: '',
      currency: 'CNY',
      payment_method: 'Cash',
      date_received: new Date().toISOString().split('T')[0],
      admin_notes: ''
    });
    setShowGiftModal(true);
  };

  const handleEditGift = (gift) => {
    setEditingGift(gift);
    setGiftFormData({
      rsvp_id: gift.rsvp_id,
      amount: gift.amount,
      currency: gift.currency,
      payment_method: gift.payment_method || '',
      date_received: gift.date_received || new Date().toISOString().split('T')[0],
      admin_notes: gift.admin_notes || ''
    });
    setShowGiftModal(true);
  };

  const handleSaveGift = async () => {
    if (!giftFormData.rsvp_id || !giftFormData.amount) {
      alert('请选择宾客并填写礼金金额');
      return;
    }

    try {
      const token = getToken();
      const response = await fetch('/api/gifts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(giftFormData)
      });

      const data = await response.json();
      if (data.success) {
        setShowGiftModal(false);
        await fetchGifts();
        await fetchStats();
        await fetchAllGuests();
      } else {
        alert(data.error || '保存失败');
      }
    } catch (err) {
      alert('保存失败，请重试');
    }
  };

  const handleDeleteGift = async (rsvpId) => {
    if (!window.confirm('确定要删除这条礼金记录吗？')) return;

    try {
      const token = getToken();
      const response = await fetch(`/api/gifts/${rsvpId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        await fetchGifts();
        await fetchStats();
        await fetchAllGuests();
      } else {
        alert(data.error || '删除失败');
      }
    } catch (err) {
      alert('删除失败，请重试');
    }
  };

  const handleExport = async () => {
    try {
      const token = getToken();
      const response = await fetch('/api/gifts/export', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'wedding-gifts.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      alert('导出失败，请重试');
    }
  };

  const filteredGifts = gifts.filter(gift =>
    gift.guest_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatCurrency = (amount, currency) => {
    const symbols = {
      'USD': '$',
      'CNY': '¥',
      'EUR': '€'
    };
    return `${symbols[currency] || ''}${Number(amount).toLocaleString()}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-gray-600">加载中...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistics Dashboard */}
      {stats && (
        <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">礼金统计</h2>
          <div className="mb-2 text-sm text-gray-600">
            💡 可记录所有宾客的礼金（包括参加和不参加婚礼的宾客）
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">
                {stats.overall.total_gifts || 0}
              </div>
              <div className="text-sm text-gray-600">已记录礼金</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">
                {formatCurrency(stats.overall.total_usd || 0, 'USD')}
              </div>
              <div className="text-sm text-gray-600">USD总计</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-red-600">
                {formatCurrency(stats.overall.total_cny || 0, 'CNY')}
              </div>
              <div className="text-sm text-gray-600">CNY总计</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600">
                {stats.overall.recorded_count || 0}/{stats.overall.total_attending || 0}
              </div>
              <div className="text-sm text-gray-600">已记录/总出席</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-orange-600">
                {stats.overall.gifts_from_not_attending || 0}
              </div>
              <div className="text-sm text-gray-600">不参加但送礼</div>
            </div>
          </div>

          {stats.by_method && stats.by_method.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">付款方式统计：</h3>
              <div className="flex flex-wrap gap-2">
                {stats.by_method.map((method, index) => (
                  <div key={index} className="bg-white px-3 py-1 rounded-lg shadow-sm">
                    <span className="text-sm font-medium text-gray-800">
                      {method.payment_method || '未指定'}
                    </span>
                    <span className="text-xs text-gray-600 ml-2">
                      ({method.count}笔 • {formatCurrency(method.total_amount, method.currency)})
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Controls */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex flex-wrap gap-4 items-center">
          <button
            onClick={handleAddGift}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
          >
            ➕ 记录礼金
          </button>
          <button
            onClick={handleExport}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            📥 导出CSV
          </button>
          <input
            type="text"
            placeholder="搜索宾客姓名..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 min-w-[200px] px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
          />
          <button
            onClick={() => {
              fetchGifts();
              fetchStats();
            }}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
          >
            🔄 刷新
          </button>
        </div>
      </div>

      {/* Gift List Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  宾客姓名
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  人数
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  礼金金额
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  付款方式
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  日期
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  餐桌
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredGifts.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                    {searchTerm ? '没有匹配的礼金记录' : '还没有记录任何礼金'}
                  </td>
                </tr>
              ) : (
                filteredGifts.map(gift => (
                  <tr key={gift.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">{gift.guest_name}</span>
                        {gift.attending ? (
                          <span className="px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded-full">参加</span>
                        ) : (
                          <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">不参加</span>
                        )}
                      </div>
                      {gift.admin_notes && (
                        <div className="text-xs text-gray-500 mt-1">📝 {gift.admin_notes}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      👥 {gift.party_size}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-green-600">
                        {formatCurrency(gift.amount, gift.currency)}
                      </div>
                      <div className="text-xs text-gray-500">{gift.currency}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {gift.payment_method || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {gift.date_received || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {gift.table_number ? `餐桌 ${gift.table_number}` : '未分配'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditGift(gift)}
                          className="text-blue-600 hover:text-blue-900"
                          title="编辑"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDeleteGift(gift.rsvp_id)}
                          className="text-red-600 hover:text-red-900"
                          title="删除"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Gift Modal */}
      {showGiftModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">
              {editingGift ? '编辑礼金记录' : '记录礼金'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  宾客姓名 *
                </label>
                <select
                  value={giftFormData.rsvp_id}
                  onChange={(e) => setGiftFormData({ ...giftFormData, rsvp_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  disabled={!!editingGift}
                >
                  <option value="">请选择宾客</option>
                  {allGuests.map(guest => (
                    <option key={guest.id} value={guest.id}>
                      {guest.name} ({guest.guests}位) - {guest.attending ? '✓ 参加' : '✗ 不参加'} {guest.has_gift && !editingGift ? ' [已记录]' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  礼金金额 *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={giftFormData.amount}
                  onChange={(e) => setGiftFormData({ ...giftFormData, amount: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="例如: 500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  货币
                </label>
                <select
                  value={giftFormData.currency}
                  onChange={(e) => setGiftFormData({ ...giftFormData, currency: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="CNY">CNY (¥)</option>
                  <option value="USD">USD ($)</option>
                  <option value="EUR">EUR (€)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  付款方式
                </label>
                <select
                  value={giftFormData.payment_method}
                  onChange={(e) => setGiftFormData({ ...giftFormData, payment_method: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="Cash">现金 (Cash)</option>
                  <option value="WeChat">微信 (WeChat)</option>
                  <option value="Alipay">支付宝 (Alipay)</option>
                  <option value="Check">支票 (Check)</option>
                  <option value="Bank Transfer">银行转账 (Bank Transfer)</option>
                  <option value="Other">其他 (Other)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  收到日期
                </label>
                <input
                  type="date"
                  value={giftFormData.date_received}
                  onChange={(e) => setGiftFormData({ ...giftFormData, date_received: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  备注
                </label>
                <textarea
                  value={giftFormData.admin_notes}
                  onChange={(e) => setGiftFormData({ ...giftFormData, admin_notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  rows="3"
                  placeholder="内部备注..."
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleSaveGift}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
              >
                保存
              </button>
              <button
                onClick={() => setShowGiftModal(false)}
                className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition"
              >
                取消
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GiftTracker;

